import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../services/apiClient'

interface Hotel {
  admin_id: number
  email: string
  full_name: string
  hotel_name: string
  phone: string
  is_active: boolean
  has_active_subscription: boolean
  subscription_expires_at: string | null
  subscription_status: string
  days_remaining: number
  hotel_templates_path: string
  created_at: string
  templates_path_exists: boolean
}

interface Template {
  template_id: number
  file_name: string
  file_path: string
  file_size: number
  template_type: string
  description: string
  is_approved: boolean
  created_at: string
  admin_id: number
  admin_name: string
  hotel_name: string
}

interface Stats {
  total_hotels: number
  pending_templates: number
  active_subscriptions: number
}

interface HotelTemplate {
  filename: string
  size: number
  modified: string
}

interface Lead {
  id: number
  name: string
  phone: string
  hotel_name: string
  message: string | null
  status: string
  created_at: string
}

export default function OwnerDashboardPage() {
  const navigate = useNavigate()
  const isMounted = useRef(true)
  
  const [activeTab, setActiveTab] = useState<'hotels' | 'templates' | 'stats' | 'hotel-templates' | 'leads'>('hotels')
  const [hotels, setHotels] = useState<Hotel[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [leads, setLeads] = useState<Lead[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  
  // Hotel templates management
  const [selectedHotel, setSelectedHotel] = useState<string>('')
  const [hotelTemplates, setHotelTemplates] = useState<HotelTemplate[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const loadData = useCallback(async () => {
    try {
      if (!isMounted.current) return
      
      setIsLoading(true)
      setError('')

      // Load stats
      const statsRes = await apiClient.get('/owner/stats')
      if (isMounted.current) setStats(statsRes.stats)

      // Load hotels
      const hotelsRes = await apiClient.get('/owner/hotels')
      console.log('Hotels response:', hotelsRes)
      if (isMounted.current) setHotels(hotelsRes.hotels || [])

      // Load templates
      const templatesRes = await apiClient.get('/owner/templates')
      if (isMounted.current) setTemplates(templatesRes.templates || [])

      // Load leads
      try {
        const leadsRes = await apiClient.get('/owner/leads')
        if (isMounted.current) setLeads(leadsRes.data?.leads || [])
      } catch (err) {
        console.error('Error loading leads:', err)
        // Не прерываем загрузку если leads не загрузились
      }
    } catch (err: any) {
      if (isMounted.current) {
        setError('Ошибка загрузки данных')
        console.error(err)
      }
    } finally {
      if (isMounted.current) setIsLoading(false)
    }
  }, [apiClient])

  useEffect(() => {
    const ownerToken = localStorage.getItem('owner_token')
    if (!ownerToken) {
      navigate('/owner-login')
      return
    }

    isMounted.current = true
    loadData()

    return () => {
      isMounted.current = false
    }
  }, [navigate, loadData])

  const handleDeleteHotel = async (adminId: number, hotelName: string) => {
    if (!window.confirm(`Вы уверены, что хотите удалить отель "${hotelName}"?\n\nЭто действие удалит:\n- Аккаунт администратора\n- Папку с шаблонами\n- Все связанные данные`)) {
      return
    }

    try {
      await apiClient.delete(`/owner/hotels/${adminId}`)
      // Reload data
      if (isMounted.current) await loadData()
      window.alert('Отель успешно удален')
    } catch (err: any) {
      window.alert('Ошибка при удалении отеля')
      console.error(err)
    }
  }

  const handleActivateSubscription = async (adminId: number, hotelName: string) => {
    if (!window.confirm(`Активировать подписку для отеля "${hotelName}"?\n\nПодписка будет активна на 1 год.`)) {
      return
    }

    try {
      await apiClient.post(`/owner/hotels/${adminId}/activate-subscription`)
      // Reload data
      if (isMounted.current) await loadData()
      window.alert('✅ Подписка успешно активирована!')
    } catch (err: any) {
      window.alert('❌ Ошибка при активации подписки')
      console.error(err)
    }
  }

  const handleDeactivateSubscription = async (adminId: number, hotelName: string) => {
    if (!window.confirm(`Деактивировать подписку для отеля "${hotelName}"?\n\nПодписка будет приостановлена (заморожены оплаченные дни). Можно снова активировать позже.`)) {
      return
    }

    try {
      console.log('🔴 Deactivating subscription for admin:', adminId)
      const response = await apiClient.post(`/owner/hotels/${adminId}/deactivate-subscription`)
      console.log('🔴 Deactivate response:', response)
      // Reload data
      if (isMounted.current) await loadData()
      window.alert('✅ Подписка успешно деактивирована!')
    } catch (err: any) {
      console.error('🔴 Deactivate error:', err)
      window.alert('❌ Ошибка при деактивации подписки')
      console.error(err)
    }
  }

  const handleApproveTemplate = async (templateId: number) => {
    try {
      await apiClient.post(`/owner/templates/${templateId}/approve`)
      // Reload data
      if (isMounted.current) await loadData()
    } catch (err: any) {
      alert('Ошибка при одобрении шаблона')
      console.error(err)
    }
  }

  const handleRejectTemplate = async (templateId: number, fileName: string) => {
    if (!window.confirm(`Отклонить и удалить шаблон "${fileName}"?`)) {
      return
    }

    try {
      await apiClient.delete(`/owner/templates/${templateId}`)
      // Reload data
      if (isMounted.current) await loadData()
    } catch (err: any) {
      window.alert('Ошибка при отклонении шаблона')
      console.error(err)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('owner_token')
    localStorage.removeItem('owner_username')
    localStorage.removeItem('admin.access_token')
    localStorage.removeItem('admin.email')
    localStorage.removeItem('admin.full_name')
    localStorage.removeItem('admin.hotel_name')
    navigate('/owner-login')
  }

  const handleCleanupOrphanedFolders = async () => {
    if (!window.confirm('Очистить осиротевшие папки шаблонов?\n\nЭто удалит папки, которые не связаны с существующими администраторами.')) {
      return
    }

    try {
      const response = await apiClient.delete('/owner/cleanup-orphaned-folders')
      window.alert(`✅ Очистка завершена!\n\nУдалено папок: ${response.deleted_count}`)
      // Reload data
      await loadData()
    } catch (err: any) {
      window.alert('Ошибка при очистке папок')
      console.error(err)
    }
  }

  // Hotel templates management functions
  const loadHotelTemplates = async (hotelName: string) => {
    try {
      setIsLoading(true)
      const response = await apiClient.get(`/owner/hotel-templates/${hotelName}`)
      setHotelTemplates(response.templates || [])
      setSelectedHotel(hotelName)
    } catch (err: any) {
      setError('Ошибка при загрузке шаблонов отеля')
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadTemplate = async (filename: string) => {
    try {
      const response = await apiClient.get(`/owner/hotel-templates/${selectedHotel}/${filename}`, {
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err: any) {
      window.alert('Ошибка при скачивании шаблона')
      console.error(err)
    }
  }

  const handleDeleteTemplate = async (filename: string) => {
    if (!window.confirm(`Удалить шаблон "${filename}"?`)) {
      return
    }

    try {
      await apiClient.delete(`/owner/hotel-templates/${selectedHotel}/${filename}`)
      // Reload templates
      await loadHotelTemplates(selectedHotel)
    } catch (err: any) {
      window.alert('Ошибка при удалении шаблона')
      console.error(err)
    }
  }

  const handleUploadTemplate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.docx')) {
      window.alert('Только файлы .docx поддерживаются')
      return
    }

    try {
      setIsUploading(true)
      const formData = new FormData()
      formData.append('file', file)

      await apiClient.post(`/owner/hotel-templates/${selectedHotel}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      // Reload templates
      await loadHotelTemplates(selectedHotel)
      window.alert('Шаблон успешно загружен')
    } catch (err: any) {
      window.alert('Ошибка при загрузке шаблона')
      console.error(err)
    } finally {
      setIsUploading(false)
      // Reset file input
      event.target.value = ''
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-purple-600">
        <div className="text-white text-2xl">Загрузка...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header with logout button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Панель владельца</h1>
        <button
          onClick={handleLogout}
          className="px-3 sm:px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium transition text-sm sm:text-base touch-manipulation"
        >
          Выход
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="text-2xl sm:text-3xl font-bold text-purple-600">{stats.total_hotels}</div>
              <div className="text-xs sm:text-sm text-gray-600">Всего отелей</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="text-2xl sm:text-3xl font-bold text-yellow-600">{stats.pending_templates}</div>
              <div className="text-xs sm:text-sm text-gray-600">Шаблонов на рассмотрении</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="text-2xl sm:text-3xl font-bold text-green-600">{stats.active_subscriptions}</div>
              <div className="text-xs sm:text-sm text-gray-600">Активных подписок</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex gap-2 sm:gap-4 border-b border-gray-200 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
          <button
            onClick={() => setActiveTab('hotels')}
            className={`pb-3 sm:pb-4 px-3 sm:px-6 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'hotels' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600'
            }`}
          >
            🏨 Отели
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`pb-3 sm:pb-4 px-3 sm:px-6 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'templates' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600'
            }`}
          >
            📄 Шаблоны
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`pb-3 sm:pb-4 px-3 sm:px-6 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'stats' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600'
            }`}
          >
            📊 Статистика
          </button>
          <button
            onClick={() => setActiveTab('hotel-templates')}
            className={`pb-3 sm:pb-4 px-3 sm:px-6 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'hotel-templates' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600'
            }`}
          >
            🏨 Шаблоны отелей
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`pb-3 sm:pb-4 px-3 sm:px-6 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'leads' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600'
            }`}
          >
            📞 Заявки
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm">
            ⚠️ {error}
          </div>
        )}

        {activeTab === 'hotels' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Управление отелями</h2>
              <button
                onClick={handleCleanupOrphanedFolders}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm touch-manipulation"
              >
                🧹 Очистить папки
              </button>
            </div>
            {hotels.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 sm:p-8 text-center text-gray-500 text-sm sm:text-base">
                Нет зарегистрированных отелей
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Отель</th>
                        <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Админ</th>
                        <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Email</th>
                        <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Создан</th>
                        <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {hotels.map((hotel) => (
                      <tr key={hotel.admin_id}>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="font-medium text-gray-900 text-sm sm:text-base">{hotel.hotel_name}</div>
                          <div className="text-xs sm:text-sm text-gray-500">
                            {hotel.templates_path_exists ? '📁 Папка создана' : '⚠️ Папка отсутствует'}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-900 text-sm sm:text-base">{hotel.full_name}</td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-900 text-sm sm:text-base hidden sm:table-cell">{hotel.email}</td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          {hotel.subscription_status === 'active' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✅ Активна
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              ❌ Неактивна
                            </span>
                          )}
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-gray-500 text-xs sm:text-sm hidden sm:table-cell">
                          {new Date(hotel.created_at).toLocaleDateString('ru-RU')}
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              onClick={() => handleDeleteHotel(hotel.admin_id, hotel.hotel_name)}
                              className="text-red-600 hover:text-red-800 font-medium text-xs sm:text-sm touch-manipulation"
                            >
                              🗑️ Удалить
                            </button>
                            {hotel.subscription_status === 'active' ? (
                              <button
                                onClick={() => handleDeactivateSubscription(hotel.admin_id, hotel.hotel_name)}
                                className="text-orange-600 hover:text-orange-800 font-medium text-xs sm:text-sm touch-manipulation"
                              >
                                🚫 Деактивировать
                              </button>
                            ) : (
                              <button
                                onClick={() => handleActivateSubscription(hotel.admin_id, hotel.hotel_name)}
                                className="text-green-600 hover:text-green-800 font-medium text-xs sm:text-sm touch-manipulation"
                              >
                                💳 Активировать
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Шаблоны на рассмотрении</h2>
            {templates.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                Нет шаблонов на рассмотрении
              </div>
            ) : (
              <div className="space-y-4">
                {templates.map((template) => (
                  <div key={template.template_id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {template.file_name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          От: {template.admin_name} ({template.hotel_name})
                        </p>
                        <p className="text-sm text-gray-500">
                          Тип: {template.template_type}
                        </p>
                        {template.description && (
                          <p className="text-sm text-gray-600 mt-2">
                            {template.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Загружен: {new Date(template.created_at).toLocaleString('ru-RU')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApproveTemplate(template.template_id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                        >
                          ✅ Одобрить
                        </button>
                        <button
                          onClick={() => handleRejectTemplate(template.template_id, template.file_name)}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                        >
                          ❌ Отклонить
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'stats' && stats && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Статистика</h2>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-600">{stats.total_hotels}</div>
                  <div className="text-gray-600">Всего отелей</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-600">{stats.pending_templates}</div>
                  <div className="text-gray-600">Шаблонов на рассмотрении</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-600">{stats.active_subscriptions}</div>
                  <div className="text-gray-600">Активных подписок</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hotel-templates' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Управление шаблонами отелей</h2>
            
            {/* Hotel selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Выберите отель:</label>
              <select
                value={selectedHotel}
                onChange={(e) => loadHotelTemplates(e.target.value)}
                className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">-- Выберите отель --</option>
                {hotels.map((hotel) => (
                  <option key={hotel.admin_id} value={hotel.hotel_name}>
                    {hotel.hotel_name}
                  </option>
                ))}
              </select>
            </div>

            {selectedHotel && (
              <>
                {/* Upload template */}
                <div className="mb-6 p-4 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">Загрузить новый шаблон</h3>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".docx"
                      onChange={handleUploadTemplate}
                      disabled={isUploading}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    {isUploading && (
                      <span className="text-purple-600">Загрузка...</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Поддерживаются только файлы .docx</p>
                </div>

                {/* Templates list */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b">
                    <h3 className="text-lg font-semibold">Шаблоны отеля: {selectedHotel}</h3>
                  </div>
                  
                  {hotelTemplates.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      В папке шаблонов нет файлов
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Имя файла</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Размер</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Изменен</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {hotelTemplates.map((template) => (
                          <tr key={template.filename} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <span className="font-medium text-gray-900">{template.filename}</span>
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {(template.size / 1024).toFixed(2)} KB
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {new Date(template.modified).toLocaleString('ru-RU')}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleDownloadTemplate(template.filename)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                                >
                                  📥 Скачать
                                </button>
                                <button
                                  onClick={() => handleDeleteTemplate(template.filename)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                >
                                  🗑️ Удалить
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'leads' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Заявки с лендинга</h2>
            {leads.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                Нет заявок
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Отель</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Имя</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Телефон</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Сообщение</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {leads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <span className="font-medium text-gray-900">{lead.hotel_name}</span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {lead.name}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          <a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline">
                            {lead.phone}
                          </a>
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {lead.message || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            lead.status === 'new' ? 'bg-green-100 text-green-800' :
                            lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
                            lead.status === 'converted' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {lead.status === 'new' ? '🆕 Новая' :
                             lead.status === 'contacted' ? '📞 Связались' :
                             lead.status === 'converted' ? '✅ Конвертирована' :
                             lead.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {new Date(lead.created_at).toLocaleString('ru-RU')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
